import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useLogoLayout } from '../contexts/LogoLayoutContext';

// Splash logo intrinsic size (matches the brand mark proportions).
const LOGO_W = 201;
const LOGO_H = 179;

// How long the brand mark holds in the centre before it glides up into the
// destination page's logo slot.
const HOLD_MS = 1900;

const GLIDE_TRANSITION = { duration: 0.75, ease: [0.22, 1, 0.36, 1] };
const FADE_TRANSITION = { duration: 0.5, ease: [0.4, 0, 0.2, 1] };

/**
 * SplashScreen renders the Allure brand intro. Instead of fading the logo out
 * and letting the destination page pop its own logo in, the splash logo now
 * performs a shared-element glide: it animates from the centred splash
 * position straight into the destination page's <RouteTransitionLogo /> slot
 * (login / register), shrinking + sliding up to land exactly where the global
 * <FloatingLogo /> will take over. The white backdrop + wordmark fade away
 * underneath the gliding logo, revealing the page beneath.
 */
export default function SplashScreen({ onComplete }) {
  const { getActiveSlot } = useLogoLayout();
  const location = useLocation();
  const spacerRef = useRef(null);
  const completedRef = useRef(false);

  // Logo geometry (fixed-position, driven imperatively).
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const w = useMotionValue(LOGO_W);
  const h = useMotionValue(LOGO_H);
  const logoOpacity = useMotionValue(0);

  // Backdrop + wordmark/tagline opacity.
  const bgOpacity = useMotionValue(1);
  const textOpacity = useMotionValue(0);

  // Anchor the logo to the invisible spacer's measured rect so the initial
  // centred placement is pixel-perfect across screen sizes.
  useLayoutEffect(() => {
    const node = spacerRef.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    x.set(r.left);
    y.set(r.top);
    w.set(r.width);
    h.set(r.height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entrance: fade the logo + wordmark in.
  useEffect(() => {
    const c1 = animate(logoOpacity, 1, { duration: 0.6, ease: 'easeOut' });
    const c2 = animate(textOpacity, 1, { duration: 0.6, delay: 0.25, ease: 'easeOut' });
    return () => {
      c1.stop();
      c2.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After the hold, glide the logo into the destination slot while the
  // backdrop + text fade out.
  useEffect(() => {
    const finish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      if (onComplete) onComplete();
    };

    const timer = setTimeout(() => {
      const slot = getActiveSlot(location.pathname);

      // Fade the white backdrop + wordmark away beneath the logo.
      animate(bgOpacity, 0, FADE_TRANSITION);
      animate(textOpacity, 0, { ...FADE_TRANSITION, duration: 0.35 });

      if (!slot) {
        // No destination logo slot (e.g. a slot-less landing) — fall back to
        // a graceful fade of the logo too.
        const c = animate(logoOpacity, 0, FADE_TRANSITION);
        c.finished.catch(() => {}).finally(finish);
        return;
      }

      const r = slot.getBoundingClientRect();
      const controls = [
        animate(x, r.left, GLIDE_TRANSITION),
        animate(y, r.top, GLIDE_TRANSITION),
        animate(w, r.width, GLIDE_TRANSITION),
        animate(h, r.height, GLIDE_TRANSITION),
      ];
      Promise.all(controls.map((c) => c.finished)).catch(() => {}).finally(finish);
    }, HOLD_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="app-shell"
      data-testid="splash-screen"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* White backdrop (fades out beneath the gliding logo) */}
      <motion.div
        style={{ position: 'absolute', inset: 0, backgroundColor: 'var(--app-bg)', opacity: bgOpacity }}
      />

      {/* Centred column: invisible logo spacer + wordmark (for measurement + layout) */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: textOpacity,
        }}
      >
        {/* Invisible spacer reserves the logo's centred rect */}
        <div ref={spacerRef} style={{ width: LOGO_W, height: LOGO_H }} aria-hidden="true" />

        <motion.h1
          initial={{ y: 10 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
          style={{
            fontFamily: "'Amarante', serif",
            fontSize: 48,
            fontWeight: 400,
            color: 'var(--text)',
            letterSpacing: '0.08em',
            marginTop: 16,
            textAlign: 'center',
          }}
        >
          ALLURE
        </motion.h1>
      </motion.div>

      {/* Tagline near the bottom (fades with the rest of the chrome) */}
      <motion.p
        style={{
          fontFamily: "'Gabriela', serif",
          fontSize: 15,
          fontWeight: 400,
          color: 'var(--text)',
          position: 'absolute',
          bottom: 120,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: textOpacity,
        }}
      >
        Designed To Experience
      </motion.p>

      {/* The gliding brand mark — animates from centre into the page logo slot */}
      <motion.img
        src="/allure-logo.png"
        alt="Allure"
        data-testid="splash-logo"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          x,
          y,
          width: w,
          height: h,
          opacity: logoOpacity,
          objectFit: 'contain',
          zIndex: 10001,
          willChange: 'transform, width, height',
        }}
      />
    </div>
  );
}
